import type { DatasetProcessing, DatasetProcessingAxisState } from "@/api";

/**
 * 파생 상태(`Dataset.processing`) 축의 표시 어휘.
 *
 * 업로드가 1차 commit 으로 데이터셋을 공개한 뒤에도 파생 처리(에피소드 진단)는
 * 계속 실행된다. 화면 여러 곳(상세 카드, 헤더 배지)이 이 단계를 각자 해석하면 같은 상태를
 * 다른 말로 부른다. 그래서 어휘를 여기 한 곳에 둔다.
 *
 * 파생 상태는 정보이고 사용 게이트가 아니다. 학습 제출과 다운로드는 이 축을 보지 않는다.
 * 진단 산출물은 학습 입력도 다운로드 대상도 아니다. 그래서 산출 중에 써도 결과가 같다.
 *
 * Owns: 축·상태 표시명, 헤더 배지 요약.
 * Does not own: 진단 결과 자체의 상태 문구(`diagnostics/diagnosticsStatusText.ts`).
 * 그 모듈은 컬렉션/에피소드 단위 `DiagnosticsStatus` 로 상태를 표시한다.
 */

/** 화면에 그리는 축. `labeling`/`annotation` 은 항상 not_configured 인 예약 축이므로 뺀다. */
export const DATASET_PROCESSING_AXES = ["parquet"] as const;

export type DatasetProcessingAxis = (typeof DATASET_PROCESSING_AXES)[number];

/** 축 표시명. 내부 축 이름(parquet)이 아니라 사용자가 기다리는 산출물의 이름을 쓴다. */
export const DATASET_PROCESSING_AXIS_LABELS: Record<DatasetProcessingAxis, string> = {
  parquet: "에피소드 진단",
};

export const DATASET_PROCESSING_STATE_LABELS: Record<DatasetProcessingAxisState, string> = {
  not_computed: "산출 안 함",
  processing: "진행 중",
  ready: "준비됨",
  partial: "일부 준비",
  stale: "갱신 필요",
  failed: "실패",
  not_configured: "미구성",
  // "안 함"이 아니라 "안 하기로 함"으로 쓴다. 되돌릴 수 있는 결정임을 알린다.
  declined: "밴드 진단 안 함",
};

/** 배지·행의 색 의미. 실제 색은 소비처 CSS 가 tone 별로 매핑한다. */
export type DatasetProcessingTone = "positive" | "progress" | "danger" | "muted";

const AXIS_STATE_TONES: Record<DatasetProcessingAxisState, DatasetProcessingTone> = {
  not_computed: "muted",
  processing: "progress",
  ready: "positive",
  partial: "positive",
  stale: "muted",
  failed: "danger",
  not_configured: "muted",
  // 이 상태는 문제가 아니라 선택이다. 그래서 경고색을 쓰지 않는다.
  declined: "muted",
};

export function datasetProcessingTone(state: DatasetProcessingAxisState): DatasetProcessingTone {
  return AXIS_STATE_TONES[state];
}

export type DatasetProcessingBadge = {
  label: string;
  tone: DatasetProcessingTone;
  title: string;
};

/**
 * 상세 헤더 배지. **눈에 띄어야 하는 단계에서만** 표시한다(진행 중 / 실패).
 * 준비된 데이터셋이나 아직 실행하지 않은 데이터셋에도 배지를 표시하면 헤더에 배지가 항상 있다.
 * 그러면 사용자가 기다려야 하는 상태를 구분하기 어렵다.
 */
export function datasetProcessingBadge(
  processing: DatasetProcessing | null | undefined,
): DatasetProcessingBadge | null {
  if (processing === null || processing === undefined) {
    return null;
  }
  const processingAxes = DATASET_PROCESSING_AXES.filter(
    (axis) => processing[axis] === "processing",
  );
  if (processingAxes.length > 0) {
    return {
      label: "처리 중",
      tone: "progress",
      title: `${joinAxisLabels(processingAxes)}을(를) 산출하고 있습니다.`,
    };
  }
  const failedAxes = DATASET_PROCESSING_AXES.filter((axis) => processing[axis] === "failed");
  if (failedAxes.length > 0) {
    return {
      label: "처리 실패",
      tone: "danger",
      title: `${joinAxisLabels(failedAxes)}을(를) 산출하지 못했습니다. Dataset 자체는 정상입니다.`,
    };
  }
  return null;
}

function joinAxisLabels(axes: readonly DatasetProcessingAxis[]): string {
  return axes.map((axis) => DATASET_PROCESSING_AXIS_LABELS[axis]).join(" · ");
}
